/**
 * Stamp the public customer payable onto an LDS ourPrice / order payload.
 * Remaining-balance SSOT on getLDSPaymentUrl must see this amount, not the
 * agent ourPrice (no public markup) or a USD→ILS conversion that undercuts
 * the ILS amount shown in checkout.
 */

function round2(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return Math.round(n * 100) / 100;
}

function positiveMoney(value) {
  const n = round2(value);
  return n != null && n > 0 ? n : null;
}

export function stampPublicLdsOrderPayable(
  ourPrice,
  { agentBaseIls, markupIls, payableTotalIls } = {}
) {
  const base = ourPrice && typeof ourPrice === "object" ? { ...ourPrice } : {};
  const payable = positiveMoney(payableTotalIls);
  if (payable == null) return base;

  const agent =
    positiveMoney(agentBaseIls) ?? positiveMoney(base.public_base_price_to_agent_ils);
  const markup =
    round2(markupIls) ?? (agent != null ? round2(payable - agent) : null);

  return {
    ...base,
    ...(agent != null ? { public_base_price_to_agent_ils: agent } : null),
    ...(markup != null ? { public_markup_ils: markup } : null),
    public_final_customer_price_ils: payable,
    display_total_ils: payable,
    customer_total: payable,
    // Public LDS has no extra credit-card fee; remaining SSOT often reads these.
    priceWithCreditCardFee: payable,
    priceWithCommissionAndCreditCardFee: payable,
  };
}

export function publicLdsOrderPayableBodyFields({
  payableTotalIls,
  agentBaseIls,
  markupIls,
} = {}) {
  const payable = positiveMoney(payableTotalIls);
  if (payable == null) return {};
  const agent = positiveMoney(agentBaseIls);
  const markup =
    round2(markupIls) ?? (agent != null ? round2(payable - agent) : null);
  return {
    display_total_ils: payable,
    customer_total: payable,
    public_final_customer_price_ils: payable,
    ...(markup != null ? { public_markup_ils: markup } : {}),
  };
}

export function enrichPublicLdsOurPriceForPersist({
  ourPrice,
  publicBasePriceToAgentIls,
  publicSelectedRoomId,
  publicSelectedRoomSnapshot,
  selectedRoom,
  payableTotalIls,
  publicFinalPriceIls,
  agentFinalPriceIls,
} = {}) {
  const selectedRoomBase = Number(selectedRoom?.public_base_price_to_agent_ils);
  const resolvedRoomBase =
    Number.isFinite(selectedRoomBase) && selectedRoomBase > 0
      ? selectedRoomBase
      : publicBasePriceToAgentIls;
  const agent = agentFinalPriceIls ?? publicBasePriceToAgentIls;
  const publicFinal = publicFinalPriceIls ?? payableTotalIls;
  const markup =
    Number.isFinite(Number(publicFinal)) && Number.isFinite(Number(agent))
      ? Number(publicFinal) - Number(agent)
      : null;

  return stampPublicLdsOrderPayable(
    {
      ...(ourPrice && typeof ourPrice === "object" ? ourPrice : {}),
      public_base_price_to_agent_ils: publicBasePriceToAgentIls,
      public_selected_room_id: publicSelectedRoomId,
      public_selected_room_snapshot: publicSelectedRoomSnapshot,
      selectedRoom: {
        ...(ourPrice?.selectedRoom || selectedRoom || {}),
        public_base_price_to_agent_ils: resolvedRoomBase,
      },
    },
    {
      agentBaseIls: agent,
      markupIls: markup,
      payableTotalIls,
    }
  );
}
